import { occyLs } from '../../utils/localStorage';
import {
  DEFAULT_DENIAL_BATCH_MS,
  PERMISSION_DENIED_MODAL_PREFERENCES_KEY,
  SUPPRESSED_GROUP_DENIAL_BATCH_MS,
} from './constants';
import {
  normalizePermissions,
  permissionSetKey,
  permissionSetsEqual,
} from './permission-set';

function parsePreferences() {
  const raw = occyLs.getObject(PERMISSION_DENIED_MODAL_PREFERENCES_KEY);
  if (!Array.isArray(raw)) return [];

  const deduped = [];
  for (const entry of raw) {
    if (!entry || entry.suppressModal !== true) continue;
    const permissions = normalizePermissions(entry.permissions);
    if (!permissions.length) continue;

    const normalizedEntry = { permissions, suppressModal: true };
    if (deduped.some((e) => permissionSetsEqual(e.permissions, permissions))) {
      continue;
    }
    deduped.push(normalizedEntry);
  }
  return deduped;
}

function persistPreferences(preferences) {
  occyLs.setObject(PERMISSION_DENIED_MODAL_PREFERENCES_KEY, preferences);
}

/** @returns {{ permissions: string[], suppressModal: boolean }[]} */
export function loadSuppressionPreferences() {
  return parsePreferences();
}

/**
 * @param {string|string[]} permissions
 * @returns {boolean}
 */
export function isModalSuppressed(permissions) {
  const normalized = normalizePermissions(permissions);
  if (!normalized.length) return false;

  const key = permissionSetKey(normalized);
  return loadSuppressionPreferences().some(
    (entry) =>
      entry.suppressModal === true && permissionSetKey(entry.permissions) === key,
  );
}

/**
 * True when denied permissions are a strict subset of a stored multi-permission
 * suppression group (other group members may still be denied shortly after).
 */
export function isAwaitingSuppressedGroupMembers(deniedSoFar) {
  const denied = normalizePermissions(deniedSoFar);
  if (!denied.length) return false;

  return loadSuppressionPreferences().some((entry) => {
    const group = normalizePermissions(entry.permissions);
    if (group.length <= 1) return false;

    const deniedIsSubset = denied.every((permission) => group.includes(permission));
    const groupFullyDenied = group.every((permission) => denied.includes(permission));
    return deniedIsSubset && !groupFullyDenied;
  });
}

/** Batch window: longer while waiting for siblings in a suppressed permission group. */
export function getDenialBatchDelayMs(deniedSoFar) {
  return isAwaitingSuppressedGroupMembers(deniedSoFar)
    ? SUPPRESSED_GROUP_DENIAL_BATCH_MS
    : DEFAULT_DENIAL_BATCH_MS;
}

/**
 * Persist "don't show again" for an exact permission combination.
 * @param {string|string[]} permissions
 */
export function saveSuppressionPreference(permissions) {
  const normalized = normalizePermissions(permissions);
  if (!normalized.length) return;

  const preferences = loadSuppressionPreferences();
  const key = permissionSetKey(normalized);
  const existingIndex = preferences.findIndex(
    (entry) => permissionSetKey(entry.permissions) === key,
  );

  const entry = { permissions: normalized, suppressModal: true };
  if (existingIndex >= 0) {
    preferences[existingIndex] = entry;
  } else {
    preferences.push(entry);
  }
  persistPreferences(preferences);
}

/** Remove all stored suppression preferences (e.g. debugging). */
export function clearSuppressionPreferences() {
  occyLs.removeItem(PERMISSION_DENIED_MODAL_PREFERENCES_KEY);
}
